import type { XpValues } from '@contracts';

/**
 * Display copy for each `XpValues` field, per settings-rewards.md §1.1's XP
 * sources table (name, "when it pays" trigger sentence, Lucide icon id) —
 * one entry per field, no hidden sources (R7.8). Order here is the ratified
 * table's row order and doubles as the row order the "How rewards work"
 * screen renders in (`RewardsExplainerView.xpSources` is keyed by the same
 * `keyof XpValues`, so this is the one place source copy lives — never
 * duplicated per screen).
 */
export interface XpSourceCopy {
  readonly label: string;
  readonly whenItPays: string;
  readonly iconId: string;
}

export const XP_SOURCE_COPY: Record<keyof XpValues, XpSourceCopy> = {
  checkinCompleted: {
    label: 'Daily check-in',
    whenItPays: 'Finishing "Done for today" — pays the same whatever the habit results.',
    iconId: 'calendar-check',
  },
  habitCompleted: {
    label: 'Habit completion',
    whenItPays: 'Each build habit done.',
    iconId: 'check',
  },
  avoidCleanDay: {
    label: 'Clean day',
    whenItPays: 'Each explicit clean day logged on an avoid habit.',
    iconId: 'shield-check',
  },
  weeklyTargetBonus: {
    label: 'Weekly target bonus',
    whenItPays:
      'A week that meets the plan — a build habit reaching its target, or an avoid habit staying within its allowance.',
    iconId: 'star',
  },
  reviewCompleted: {
    label: 'Weekly review',
    whenItPays: 'Finishing the review — full value even if you finish it late.',
    iconId: 'calendar-range',
  },
  adjustmentAccepted: {
    label: 'Smart adjustment',
    whenItPays: 'Accepting a target adjustment in the review — recalibrating pays, always.',
    iconId: 'sliders-horizontal',
  },
  actionCompleted: {
    label: 'Action completed',
    whenItPays: 'Each objective action completed — same granularity as a habit completion.',
    iconId: 'list-checks',
  },
  dateAdjustmentAccepted: {
    label: 'Date adjustment accepted',
    whenItPays:
      'Accepting a suggested target-date or pace change on an objective — the date-realism counterpart to Smart adjustment.',
    iconId: 'calendar-cog',
  },
  dayClosed: {
    label: 'Close the day',
    whenItPays: 'Closing the day from the Today header — pays in full no matter how late.',
    iconId: 'moon-star',
  },
  priorityCompleted: {
    label: 'Priority completed',
    whenItPays:
      'Finishing a standalone Priority — one linked to an objective action pays through Action completed instead, never both.',
    iconId: 'list-todo',
  },
};
